import * as React from "react";
import styles from "./styles/index.module.scss";
import { isMobile } from "../utils/pathHelper";

interface IProps {
	page: number;
	hasNext: boolean;
	hasPrev: boolean;
	totalItems: number;
	itemsPerPage: number;
	handlePageChange: (page: number) => void;
}

const Pagination: React.FC<IProps> = ({
	page,
	hasNext,
	hasPrev,
	totalItems,
	itemsPerPage,
	handlePageChange,
}) => {
	const totalPages = Math.ceil(totalItems / itemsPerPage);

	if (totalPages <= 1) {
		return null;
	}

	function handlePrevClick(event: React.MouseEvent) {
		event.preventDefault();
		if (hasPrev) {
			handlePageChange(page - 1);
		}
	}

	function handleNextClick(event: React.MouseEvent) {
		event.preventDefault();
		if (hasNext) {
			handlePageChange(page + 1);
		}
	}

	function handlePageClick(pageNumber: number) {
		return (event: React.MouseEvent) => {
			event.preventDefault();
			if (pageNumber !== page) {
				handlePageChange(pageNumber);
			}
		};
	}

	// 表示するページ番号を計算（現在のページの前後5ページ + 最初と最後のページ + 省略記号）
	const getVisiblePageNumbers = (): (number | null)[] => {
		const delta = isMobile() ? 2 : 5;
		const start = Math.max(1, page - delta);
		const end = Math.min(totalPages, page + delta);

		const pages: (number | null)[] = [];

		// 最初のページを追加
		pages.push(1);

		// 最初のページと表示範囲の開始が離れている場合は省略記号を追加
		if (start > 2) {
			pages.push(null);
		}

		// 中間のページ番号を追加（1と最終ページは別で追加するのでスキップ）
		for (let i = start; i <= end; i++) {
			if (i !== 1 && i !== totalPages) {
				pages.push(i);
			}
		}

		// 表示範囲の終了と最終ページが離れている場合は省略記号を追加
		if (end < totalPages - 1) {
			pages.push(null);
		}

		// 最終ページを追加（1ページしかない場合は重複を避ける）
		if (totalPages > 1) {
			pages.push(totalPages);
		}

		return pages;
	};

	const pageNumbers = getVisiblePageNumbers();

	return (
		<div className={styles.paginationContainer}>
			<div className={styles.paginationWrapper}>
				<button
					onClick={handlePrevClick}
					className={`${styles.paginationArrow} ${!hasPrev ? styles.disabled : ""}`}
					disabled={!hasPrev}
				>
					<i className="fas fa-chevron-left" />
				</button>

				{pageNumbers.map((pageNumber, index) =>
					pageNumber === null ? (
						<span key={`ellipsis-${index}`} className={styles.ellipsis}>
							...
						</span>
					) : (
						<button
							key={pageNumber}
							onClick={handlePageClick(pageNumber)}
							className={`${styles.pageNumber} ${pageNumber === page ? styles.active : ""}`}
						>
							{pageNumber}
						</button>
					),
				)}

				<button
					onClick={handleNextClick}
					className={`${styles.paginationArrow} ${!hasNext ? styles.disabled : ""}`}
					disabled={!hasNext}
				>
					<i className="fas fa-chevron-right" />
				</button>
			</div>
		</div>
	);
};

export default Pagination;
